import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

// Category hero imagery + copy. Keys match the slug used in /shop/:category.
const CATEGORY_META = {
  "sofa": {
    title: "Sofa",
    copy: "Living-room heroes — hand-tufted sofas, sofa-cum-beds, center tables and lounge chairs.",
    img: "https://images.unsplash.com/photo-1567538096630-e0c55bd6374c",
  },
  "bedroom": {
    title: "Bedroom",
    copy: "Beds, wardrobes, dressing tables and side pieces — for the quietest room in the house.",
    img: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85",
  },
  "living-room": {
    title: "Living Room",
    copy: "Coffee tables, TV units, bookshelves & seating that shape everyday gatherings.",
    img: "https://images.unsplash.com/photo-1758977404039-6e834be8eca8",
  },
  "dining": {
    title: "Dining",
    copy: "4-seater, 6-seater and family-size dining sets in solid wood.",
    img: "https://images.unsplash.com/photo-1633505899118-4ca6bd143043",
  },
  "storage": {
    title: "Storage",
    copy: "Sideboards, cabinets, wardrobes and shelves — thoughtfully organised.",
    img: "https://images.unsplash.com/photo-1594620302200-9a762244a156",
  },
  "study-office": {
    title: "Study & Office",
    copy: "Study desks, computer tables, office tables and chairs — for focus at home or work.",
    img: "https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd",
  },
  "bar": {
    title: "Bar Furniture",
    copy: "Bar cabinets, chairs and trolleys — for the well-crafted evening.",
    img: "https://images.unsplash.com/photo-1594620302200-9a762244a156",
  },
};

export default function ShopPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [subcats, setSubcats] = useState([]);
  const [activeSub, setActiveSub] = useState(searchParams.get("sub") || "all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const meta = CATEGORY_META[category] || { title: "Shop", copy: "", img: "https://images.unsplash.com/photo-1758977404039-6e834be8eca8" };

  // When category or ?sub= changes, sync activeSub
  useEffect(() => {
    setActiveSub(searchParams.get("sub") || "all");
    api.get(`/subcategories?category=${category}`)
      .then((r) => setSubcats(r.data.subcategories || []))
      .catch(() => setSubcats([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, searchParams]);

  // Keep the URL in sync when user clicks chips
  const chooseSub = (s) => {
    setActiveSub(s);
    const params = new URLSearchParams(searchParams);
    if (s === "all") params.delete("sub"); else params.set("sub", s);
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    setLoading(true);
    const params = { category, q: q || undefined };
    if (activeSub !== "all") params.subcategory = activeSub;
    api.get("/products", { params }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [category, q, activeSub]);

  return (
    <div>
      <section className="relative h-[45vh] overflow-hidden">
        <img src={meta.img} alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917]/70 via-[#1C1917]/20 to-transparent"/>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex items-end pb-12 text-white">
          <div>
            <p className="eyebrow text-[#E7D3B3]">Collection</p>
            <h1 data-testid="shop-title" className="font-serif text-5xl md:text-7xl mt-2 tracking-tight">{meta.title}</h1>
            <p className="mt-3 max-w-xl text-stone-200">{meta.copy}</p>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {subcats.length > 0 && (
          <div className="mb-8">
            <p className="text-xs tracking-widest uppercase text-stone-500 mb-3">Browse by</p>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="subcat-chip-all"
                onClick={() => chooseSub("all")}
                className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${activeSub === "all" ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"}`}
              >
                All
              </button>
              {subcats.map((s) => (
                <button
                  key={s}
                  data-testid={`subcat-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => chooseSub(s)}
                  className={`px-4 py-2 text-xs tracking-widest uppercase border transition-colors ${activeSub === s ? "bg-[#1C1917] text-white border-[#1C1917]" : "bg-transparent text-stone-700 border-stone-300 hover:border-stone-900"}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <p className="text-sm text-stone-500">
            {items.length} {items.length === 1 ? "piece" : "pieces"}{activeSub !== "all" ? ` in ${activeSub}` : ""}
          </p>
          <input data-testid="shop-search" placeholder="Search this collection" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        </div>

        {loading ? <p className="text-stone-500">Loading…</p> : (
          items.length === 0 ? (
            <div data-testid="shop-empty" className="border border-dashed border-stone-300 py-20 text-center">
              <p className="font-serif text-2xl text-stone-800">Collection coming soon</p>
              <p className="mt-2 text-sm text-stone-500 max-w-md mx-auto">Our workshop is currently adding new pieces to this collection. Please check back shortly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {items.map((p) => <ProductCard key={p.id} p={p}/>)}
            </div>
          )
        )}
      </section>
    </div>
  );
}
